<template>
  <div class="map-page">

    <!-- ════════════════════ DASHBOARD HEADER CARD ════════════════════ -->
    <div class="map-dash-header">
      <div class="map-dash-header__top">
        <div>
          <div class="map-eyebrow">SALES MAP · GEOLOCATION</div>
          <h1 class="map-title">Mapa de pedidos</h1>
        </div>
        <div class="map-header-actions">
          <div class="map-stat-pill">
            <i class="fa-solid fa-calendar-days"></i>
            <strong>{{ new Date(dateRange.startDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) }}</strong>
            <span class="map-pill-sep">→</span>
            <strong>{{ new Date(dateRange.endDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) }}</strong>
          </div>
          <div class="map-stat-pill map-stat-pill--mode">
            <i :class="tipoDatosCalcular === 'Libras' ? 'fa-solid fa-sterling-sign' : 'fa-solid fa-box'"></i>
            <span>por {{ tipoDatosCalcular === 'Libras' ? 'libras' : 'cantidad' }}</span>
          </div>
        </div>
      </div>

      <!-- KPIs -->
      <div class="map-kpis">
        <div v-for="k in kpiTiles" :key="k.key" class="map-kpi">
          <span class="map-kpi__label"><i :class="k.icon"></i> {{ k.label }}</span>
          <span class="map-kpi__value">{{ k.value }}</span>
          <span v-if="k.delta !== null" class="map-kpi__delta" :class="k.delta >= 0 ? 'is-up' : 'is-down'">
            {{ k.delta >= 0 ? '▲' : '▼' }} {{ Math.abs(k.delta).toFixed(0) }}% <em>vs {{ anyoAnterior }}</em>
          </span>
          <span v-else class="map-kpi__delta map-kpi__delta--na">—</span>
        </div>
      </div>
    </div>

    <!-- LOADING GLOBAL -->
    <div v-if="cargando" class="map-loading">
      <b-spinner label="Cargando..." type="grow" variant="primary"></b-spinner>
      <span>Cargando datos...</span>
    </div>

    <!-- GRÁFICAS PAÍSES Y CIUDADES -->
    <div class="map-section">
      <GraficaPaises :items="orders" :tipoDatosCalcular="tipoDatosCalcular" @filtrar="onChartFilter"></GraficaPaises>
    </div>

    <div class="map-section">
      <GraficaCiudades :items="orders" :tipoDatosCalcular="tipoDatosCalcular" @filtrar="onChartFilter"></GraficaCiudades>
    </div>

    <!-- FABs FLOTANTES -->
    <div class="map-fabs">
      <button class="map-fab map-fab--toggle" @click="toggleTipoDatos" :title="tipoDatosCalcular === 'Libras' ? 'Ver por cantidad' : 'Ver por libras'">
        <i v-if="tipoDatosCalcular === 'Libras'" class="fa-solid fa-sterling-sign"></i>
        <i v-else class="fa-solid fa-box"></i>
      </button>
      <button class="map-fab map-fab--calendar" @click="modalVisible = true" title="Filtrar por fecha">
        <b-spinner v-if="cargando" type="grow" style="width: 1.2rem; height: 1.2rem; color: white"></b-spinner>
        <i v-else class="fa-solid fa-calendar-days"></i>
      </button>
    </div>

    <!-- MODAL FECHA -->
    <b-modal v-model="modalVisible" title="Filtros de búsqueda" size="lg" hide-footer scrollable modal-class="map-modal">
      <b-row class="align-items-center mb-4 gx-3">
        <b-col md="5">
          <date-range-picker
            :ranges="ranges"
            :locale-data="locale"
            v-model="dateRange"
            :opens="'inline'"
            style="width: 100%"
          />
        </b-col>
      </b-row>
    </b-modal>

    <!-- FILTROS + MAPA -->
    <div class="map-section map-section--main">
      <div class="map-filters">
        <div class="map-filters__input">
          <i class="fa-solid fa-magnifying-glass"></i>
          <b-form-input
            v-model="searchQuery"
            placeholder="Buscar pedido, SKU..."
            debounce="300"
          />
        </div>
        <div class="map-filters__select">
          <v-select
            v-model="selectedCategory"
            :options="['Todas las categorías', ...categories]"
            :clearable="false"
            placeholder="Categoría"
          />
        </div>
        <button class="map-filters__reset" @click="resetMap">
          <i class="fa-solid fa-arrows-to-dot"></i> Reset
        </button>

        <!-- Filtros activos desde las gráficas -->
        <span v-if="selectedCountry" class="map-chip">
          <i class="fa-solid fa-earth-europe"></i> {{ selectedCountry }}
          <button @click="selectedCountry = ''; selectedCity = ''" title="Quitar filtro">×</button>
        </span>
        <span v-if="selectedCity" class="map-chip">
          <i class="fa-solid fa-city"></i> {{ selectedCity }}
          <button @click="selectedCity = ''" title="Quitar filtro">×</button>
        </span>
        <button v-if="hayFiltros" class="map-filters__clear" @click="limpiarFiltros">
          Limpiar filtros
        </button>
        <span class="map-filters__hint"><i class="fa-regular fa-hand-pointer"></i> Clic en una barra o sector de las gráficas para filtrar el mapa</span>
      </div>

      <div class="map-container">
        <div v-if="cargando" class="map-container__loading">
          <b-spinner type="grow" variant="primary"></b-spinner>
        </div>
        <div ref="mapContainer" class="map-container__canvas"></div>
      </div>
    </div>

    <!-- COMPARACIÓN ANUAL -->
    <div class="map-section">
      <div class="map-section__header">
        <h4 class="map-section__title">
          <span class="map-section__chevron">›</span>
          <span>Comparación anual</span>
        </h4>
        <span class="map-section__subtitle">
          <i class="fa-solid fa-arrows-left-right"></i>
          {{ formatearFechaSinAnyo(dateRange.startDate) }} — {{ formatearFechaSinAnyo(dateRange.endDate) }}
        </span>
      </div>
      <ComparacionAnyo
        :items="orders"
        :ordersOlds="ordersOlds"
        :inicio="formatearFechaSinAnyo(dateRange.startDate)"
        :fin="formatearFechaSinAnyo(dateRange.endDate)"
      ></ComparacionAnyo>
    </div>

  </div>
</template>
  
<script>
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import vSelect from "vue-select";

import axios from "@axios";

import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

import moment from "moment";

import GraficaPaises from "../../layouts/components/map/paises.vue";
import GraficaCiudades from "../../layouts/components/map/ciudades.vue";
import ComparacionAnyo from "../../layouts/components/map/comparacionAnyo.vue";

const clusterIconUrls = [
  "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png",
  "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m2.png",
  "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m3.png",
  "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m5.png",
  "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m4.png",
];

const renderer = {
  render: ({ count, position }) => {
    let index = 0;
    if (count < 10) index = 0;
    else if (count < 50) index = 1;
    else if (count < 100) index = 2;
    else if (count < 500) index = 3;
    else index = 4;

    const sizeMap = [53, 56, 59, 62, 65];
    return new google.maps.Marker({
      position,
      icon: {
        url: clusterIconUrls[index],
        scaledSize: new google.maps.Size(sizeMap[index], sizeMap[index]),
      },
      label: { text: String(count), color: "#fff", fontSize: "14px", fontWeight: "bold" },
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    });
  },
};

const MAP_CENTER = { lat: 38.294008, lng: -0.58664 }; // Alicante
const MAP_ZOOM = 3;

// Resumen de un conjunto de pedidos (ingresos y unidades por line items, como las gráficas)
const resumir = (orders) => {
  const r = { pedidos: orders.length, ingresos: 0, unidades: 0, paises: new Set() };
  orders.forEach((o) => {
    o.line_items.forEach((i) => {
      const q = Number(i.quantity) || 0;
      r.ingresos += (parseFloat(i.price) || 0) * q;
      r.unidades += q;
    });
    if (o.shipping_address && o.shipping_address.country) r.paises.add(o.shipping_address.country);
  });
  r.ticket = r.pedidos ? r.ingresos / r.pedidos : 0;
  r.paises = r.paises.size;
  return r;
};

export default {
  components: {
    vSelect,
    DateRangePicker,
    GraficaPaises,
    GraficaCiudades,
    ComparacionAnyo,
  },
  data() {
    return {
      modalVisible: false,
      map: null,
      markers: [],
      markerClusterer: null,
      searchQuery: "",
      selectedCategory: "",
      selectedCountry: "",
      selectedCity: "",
      categories: [],
      allMarkersData: [],
      modoDiario: false,
      primeraCarga: true,
      intervaloItems: null,
      dateRange: {
        startDate: moment().subtract(6, "days")._d,
        endDate: moment()._d,
      },
      locale: {
        format: "dd/mm/yyyy",
        separator: " - ",
        applyLabel: "Buscar",
        cancelLabel: "Cancelar",
        fromLabel: "Desde",
        toLabel: "Hasta",
        customRangeLabel: "Personalizado",
        daysOfWeek: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
        monthNames: [
          "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
          "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
        ],
        firstDay: 1,
      },
      ranges: {
        Hoy: [moment()._d, moment()._d],
        Ayer: [moment().subtract(1, "days")._d, moment().subtract(1, "days")._d],
        "Últimos 7 días": [moment().subtract(6, "days")._d, moment()._d],
        "Últimos 30 días": [moment().subtract(29, "days")._d, moment()._d],
        "Este mes": [moment().startOf("month")._d, moment().endOf("month")._d],
        "Mes pasado": [
          moment().subtract(1, "month").startOf("month")._d,
          moment().subtract(1, "month").endOf("month")._d,
        ],
        "Últimos 6 meses": [moment().subtract(6, "months").startOf("month")._d, moment().endOf("month")._d],
        "Últimos 12 meses": [moment().subtract(12, "months").startOf("month")._d, moment().endOf("month")._d],
      },
      cargando: false,
      orders: [],
      ordersPrevYear: [],
      ordersOlds: [],
      tipoDatosCalcular: "Libras",
    };
  },
  computed: {
    anyoAnterior() {
      return new Date(this.dateRange.endDate).getFullYear() - 1;
    },
    hayFiltros() {
      return !!(this.searchQuery || this.selectedCountry || this.selectedCity ||
        (this.selectedCategory && this.selectedCategory !== "Todas las categorías"));
    },
    kpiTiles() {
      const a = resumir(this.orders);
      const p = resumir(this.ordersPrevYear);
      const delta = (x, y) => (this.ordersPrevYear.length && y ? ((x - y) / y) * 100 : null);
      const gbp = (v) => "£" + Math.round(v).toLocaleString("es-ES");
      return [
        { key: "ingresos", label: "Ingresos", icon: "fa-solid fa-sterling-sign", value: gbp(a.ingresos), delta: delta(a.ingresos, p.ingresos) },
        { key: "pedidos", label: "Pedidos", icon: "fa-solid fa-cart-shopping", value: a.pedidos.toLocaleString("es-ES"), delta: delta(a.pedidos, p.pedidos) },
        { key: "unidades", label: "Unidades", icon: "fa-solid fa-box", value: a.unidades.toLocaleString("es-ES"), delta: delta(a.unidades, p.unidades) },
        { key: "ticket", label: "Orden media", icon: "fa-solid fa-receipt", value: gbp(a.ticket), delta: delta(a.ticket, p.ticket) },
        { key: "paises", label: "Países", icon: "fa-solid fa-earth-europe", value: String(a.paises), delta: delta(a.paises, p.paises) },
      ];
    },
  },
  async mounted() {
    await window.googleMapsReady;
    this.modoDiario = this.$route.params.id !== undefined;
    this.loaditems();
    this.initMap();

    if (this.modoDiario) {
      this.intervaloItems = setInterval(() => this.loaditems(true), 120000);
    }
  },
  beforeDestroy() {
    if (this.intervaloItems) clearInterval(this.intervaloItems);
  },
  methods: {
    formatearFechaSinAnyo(fecha) {
      if (!fecha) return "";
      return new Date(fecha).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
    },
    showMapMessage(message, position = null) {
      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="font-weight: bold; color: green;">${message}</div>`,
        position: position || this.map.getCenter(),
      });
      infoWindow.open(this.map);
    },
    // auto = recarga automática cada 2 min: sin avisos de carga ni registro de uso
    loaditems(auto = false) {
      if (!auto) axios.post("/uso", { seccion: "map" });
      if (!auto) this.cargando = true;

      const formatDate = (date) => moment(date).format("YYYY-MM-DD");
      const url = this.modoDiario
        ? "/map/dashboard?dia=1"
        : `/map/dashboard?from=${formatDate(this.dateRange.startDate)} 00:00:00&to=${formatDate(this.dateRange.endDate)} 23:59:59`;

      axios
        .get(url, { silencioso: auto })
        .then(({ data }) => {
          const nuevasOrders = data.actual;
          this.ordersPrevYear = data.anterior1;
          this.ordersOlds = [...data.anterior1, ...data.anterior2];

          const antiguosIds = new Set(this.orders.map((o) => o.id));
          const nuevasDetectadas = nuevasOrders.filter((o) => !antiguosIds.has(o.id));

          // En directo: si no hay cambios no repintamos el mapa
          if (this.modoDiario && !this.primeraCarga && nuevasDetectadas.length === 0 && nuevasOrders.length === this.orders.length) {
            return;
          }

          if (this.modoDiario && !this.primeraCarga) {
            nuevasDetectadas.forEach((nueva) => this.avisarNuevoPedido(nueva));
          }

          this.orders = nuevasOrders;
          this.markers = [];
          this.allMarkersData = [];

          this.orders.forEach((order) => {
            const addr = order.shipping_address || {};
            const lat = parseFloat(addr.latitude);
            const lng = parseFloat(addr.longitude);
            if (isNaN(lat) || isNaN(lng)) return;

            order.line_items.forEach((item, index) => {
              if (!item.image || item.draft) return;
              const offset = 0.0001;
              this.addOrder(order, { lat: lat + index * offset, lng: lng + index * offset }, item);
            });
          });

          this.primeraCarga = false;
          this.filterMarkers();
        })
        .catch((e) => console.error("Error cargando el mapa:", e))
        .finally(() => {
          this.cargando = false;
        });
    },
    avisarNuevoPedido(nueva) {
      const lat = parseFloat(nueva.shipping_address && nueva.shipping_address.latitude);
      const lng = parseFloat(nueva.shipping_address && nueva.shipping_address.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const skusHtml = nueva.line_items
        .filter((item) => item.image)
        .map((item) => `<img src="${item.image}" alt="${item.sku}" title="${item.sku}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px; margin-right: 8px;" />`)
        .join("");

      const mensaje = `
        <div style="padding: 10px 5px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); background-color: #ffffff; font-family: 'Segoe UI', sans-serif; color: #333; max-width: 250px; font-size: 14px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-weight: 600;">Nuevo pedido: ${nueva.name}</div>
            <button
              onclick="this.closest('div[role=dialog]').parentElement.style.display='none'"
              style="background: none; border: none; font-size: 18px; cursor: pointer; color: #999; transition: color 0.2s;"
              onmouseover="this.style.color='#000'"
              onmouseout="this.style.color='#999'"
            >✕</button>
          </div>
          <div style="margin-top: 8px; line-height: 1.4;">
            <small style="color: #666;">
              <div style="display: flex; gap: 8px; flex-wrap: wrap; justify-content: center;">${skusHtml}</div>
            </small>
          </div>
        </div>`;

      this.showMapMessage(mensaje, { lat, lng });
    },
    initMap() {
      this.map = new google.maps.Map(this.$refs.mapContainer, { center: MAP_CENTER, zoom: MAP_ZOOM });
      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        maxZoom: 15,
        renderer,
      });
    },
    resetMap() {
      this.map.setCenter(MAP_CENTER);
      this.map.setZoom(MAP_ZOOM);
    },
    addOrder(order, position, item) {
      const marker = new google.maps.Marker({
        position,
        title: order.name + "\n" + item.sku,
        icon: {
          url: item.image,
          size: new google.maps.Size(40, 40),
          scaledSize: new google.maps.Size(40, 40),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(20, 40),
        },
      });

      this.markers.push(marker);

      const addr = order.shipping_address || {};
      this.allMarkersData.push({
        marker,
        name: order.name.toLowerCase(),
        category: item.product_type.toLowerCase(),
        sku: (item.sku || "").toLowerCase(),
        itemName: (item.name || "").toLowerCase(),
        country: (addr.country || "").toLowerCase(),
        city: (addr.city || "").toLowerCase(),
      });

      if (item.product_type && !this.categories.includes(item.product_type)) {
        this.categories.push(item.product_type);
        this.categories.sort();
      }
    },
    filterMarkers() {
      const query = this.searchQuery.toLowerCase().trim();
      let category = this.selectedCategory.toLowerCase();
      if (category === "") category = "todas las categorías";
      const country = this.selectedCountry.toLowerCase();
      const city = this.selectedCity.toLowerCase();

      this.allMarkersData.forEach(({ marker }) => marker.setMap(null));
      this.markerClusterer.clearMarkers();

      const filteredMarkers = this.allMarkersData
        .filter((m) => {
          const matchesQuery = !query || m.name.includes(query) || m.itemName.includes(query) || m.sku.includes(query);
          const matchesCategory = category === "todas las categorías" || m.category === category;
          const matchesCountry = !country || m.country === country;
          const matchesCity = !city || m.city === city;
          return matchesQuery && matchesCategory && matchesCountry && matchesCity;
        })
        .map((m) => m.marker);

      filteredMarkers.forEach((m) => m.setMap(this.map));
      this.markerClusterer.addMarkers(filteredMarkers);
    },
    // Clic en una gráfica: { pais, ciudad, tipo, producto } — repetir el mismo clic quita el filtro
    onChartFilter({ pais, ciudad, tipo, producto }) {
      const toggle = (actual, nuevo) => (actual === nuevo ? "" : nuevo);
      if (pais !== undefined) {
        this.selectedCountry = toggle(this.selectedCountry, pais);
        if (ciudad === undefined) this.selectedCity = "";
      }
      if (ciudad !== undefined) this.selectedCity = toggle(this.selectedCity, ciudad);
      if (tipo !== undefined) this.selectedCategory = toggle(this.selectedCategory, tipo) || "Todas las categorías";
      if (producto !== undefined) this.searchQuery = toggle(this.searchQuery, producto);

      this.$refs.mapContainer.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    limpiarFiltros() {
      this.searchQuery = "";
      this.selectedCategory = "Todas las categorías";
      this.selectedCountry = "";
      this.selectedCity = "";
    },
    toggleTipoDatos() {
      this.tipoDatosCalcular = this.tipoDatosCalcular === "Libras" ? "Ventas" : "Libras";
    },
  },
  watch: {
    searchQuery() { this.filterMarkers(); },
    selectedCategory() { this.filterMarkers(); },
    selectedCountry() { this.filterMarkers(); },
    selectedCity() { this.filterMarkers(); },
    dateRange() {
      this.loaditems();
      this.modalVisible = false;
    },
  },
};
</script>

<style lang="scss">
@import "../@core/scss/vue/libs/vue-select.scss";
@import "../@core/scss/vue/libs/chart-apex.scss";

.gm-style-iw-chr {
  display: none !important;
}

body {
  font-family: "Avenir Next" !important;
}
.apexcharts-canvas .apexcharts-text {
  font-family: "Avenir Next" !important;
}
[dir=ltr] .apexcharts-canvas .apexcharts-legend-series .apexcharts-legend-text {
  font-family: "Avenir Next" !important;
}
</style>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// ══════════════════════════════════════════════════════════════
// DATA-DENSE DASHBOARD — MAP
// Same tokens as /pedido-taller-vero/fabricando
// ══════════════════════════════════════════════════════════════

$mp-bg:        #F8FAFC;
$mp-surface:   #FFFFFF;
$mp-surface-2: #F1F5F9;
$mp-border:    #E2E8F0;
$mp-divider:   #EEF2F7;
$mp-ink:       #1E293B;
$mp-text:      #1E293B;
$mp-mute:      #64748B;
$mp-soft:      #94A3B8;
$mp-primary:   #3B82F6;
$mp-primary-soft: #60A5FA;
$mp-cta:       #F97316;
$mp-emerald:   #10B981;
$mp-coral:     #EF4444;

.map-page {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background: $mp-bg;
  color: $mp-text;
  margin: -2rem;
  padding: 28px 32px 56px;
  min-height: calc(100vh - 4rem);
  font-feature-settings: "tnum", "zero";

  *, *::before, *::after { box-sizing: border-box; }
}

// ──────────── DASHBOARD HEADER CARD ────────────
.map-dash-header {
  background: $mp-surface;
  border: 1px solid $mp-border;
  border-radius: 14px;
  padding: 18px 20px 16px;
  margin-bottom: 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.map-dash-header__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.map-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: $mp-primary;
  letter-spacing: 0.14em;
  margin-bottom: 4px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: $mp-cta;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
    animation: mp-pulse 2s infinite;
  }
}

.map-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: $mp-ink;
  line-height: 1.1;
  letter-spacing: -0.01em;
  margin: 0;
}

.map-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.map-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  background: $mp-bg;
  border: 1px solid $mp-border;
  border-radius: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: $mp-mute;

  i { color: $mp-primary; font-size: 12px; }
  strong {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    color: $mp-ink;
    font-feature-settings: "tnum";
    letter-spacing: 0.01em;
  }

  .map-pill-sep {
    color: $mp-soft;
    font-weight: 400;
    margin: 0 2px;
  }

  &--mode {
    i { color: $mp-cta; }
  }
}

// ──────────── KPIs ────────────
.map-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $mp-divider;

  @media (max-width: 1100px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.map-kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: $mp-bg;
  border: 1px solid $mp-border;
  border-radius: 10px;
  min-width: 0;

  &__label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $mp-mute;
    white-space: nowrap;
    i { color: $mp-primary; margin-right: 4px; font-size: 10px; }
  }
  &__value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.1;
    color: $mp-ink;
    letter-spacing: -0.01em;
    font-feature-settings: "tnum";
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__delta {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    em { font-style: normal; font-weight: 500; color: $mp-soft; }
    &.is-up { color: $mp-emerald; }
    &.is-down { color: $mp-coral; }
    &--na { color: $mp-soft; }
  }
}

// ──────────── CHIPS DE FILTRO ────────────
.map-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 6px 0 12px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.35);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: $mp-primary;
  i { font-size: 11px; }
  button {
    width: 22px; height: 22px;
    border: 0; border-radius: 6px;
    background: transparent;
    color: $mp-primary;
    font-size: 16px; line-height: 1;
    cursor: pointer;
    &:hover { background: rgba(59, 130, 246, 0.15); }
  }
}

.map-filters__clear {
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: $mp-coral;
  cursor: pointer;
  &:hover { background: rgba(239, 68, 68, 0.08); }
}

.map-filters__hint {
  margin-left: auto;
  font-size: 11px;
  color: $mp-soft;
  i { margin-right: 4px; }
  @media (max-width: 900px) { display: none; }
}

// ──────────── LOADING ────────────
.map-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: $mp-mute;
  font-weight: 500;
  background: $mp-surface;
  border: 1px solid $mp-border;
  border-radius: 14px;
  margin-bottom: 18px;
  font-size: 13px;
}

// ──────────── SECTION CARDS ────────────
.map-section {
  background: $mp-surface;
  border: 1px solid $mp-border;
  border-radius: 14px;
  margin-bottom: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: all 200ms ease;

  &:hover { box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04); }

  &--main { padding: 20px; }
}

.map-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px 14px;
  border-bottom: 1px solid $mp-divider;
  flex-wrap: wrap;
}

.map-section__title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: $mp-ink;
  margin: 0;
  letter-spacing: -0.015em;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
}

.map-section__chevron {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 800;
  color: $mp-primary;
  line-height: 1;
}

.map-section__subtitle {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  color: $mp-mute;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  i { color: $mp-soft; font-size: 10px; }
}

// ──────────── FILTERS ────────────
.map-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.map-filters__input {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 360px;

  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $mp-soft;
    font-size: 12px;
    z-index: 2;
    pointer-events: none;
  }
}

// Los ::v-deep van bajo .map-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.map-page ::v-deep .map-filters__input input,
.map-page ::v-deep .map-filters__input .form-control {
  padding-left: 34px !important;
  border-radius: 8px !important;
  border: 1px solid $mp-border !important;
  background: $mp-surface !important;
  height: 38px !important;
  font-size: 13px !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
  color: $mp-ink !important;
  box-shadow: none !important;
  transition: all 150ms ease !important;

  &::placeholder { color: $mp-soft; }

  &:focus {
    outline: none !important;
    border-color: $mp-primary !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
  }
}

.map-filters__select {
  min-width: 220px;
}

.map-page ::v-deep .map-filters__select .v-select {
  .vs__dropdown-toggle {
    background: $mp-surface !important;
    border: 1px solid $mp-border !important;
    border-radius: 8px !important;
    min-height: 38px;
    padding: 0 6px;
    box-shadow: none !important;
  }
  &.vs--open .vs__dropdown-toggle,
  .vs__dropdown-toggle:focus-within {
    border-color: $mp-primary !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
  }
  .vs__selected, .vs__search {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $mp-ink;
    padding: 0 6px;
    line-height: 36px;
  }
  .vs__open-indicator { fill: $mp-soft; transform: scale(0.75); }
  .vs__dropdown-menu {
    background: $mp-surface;
    border: 1px solid $mp-border;
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
    padding: 4px;
  }
  .vs__dropdown-option {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $mp-text;
    padding: 8px 10px;
    border-radius: 6px;
    &--highlight { background: $mp-surface-2; color: $mp-primary; }
  }
}

.map-filters__reset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  background: $mp-surface;
  border: 1px solid $mp-border;
  border-radius: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: $mp-mute;
  cursor: pointer;
  transition: all 150ms ease;

  i { font-size: 11px; color: $mp-soft; }

  &:hover {
    background: $mp-surface-2;
    border-color: $mp-soft;
    color: $mp-ink;
    i { color: $mp-primary; }
  }
}

// ──────────── MAP CONTAINER ────────────
.map-container {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid $mp-border;
  background: $mp-surface-2;
}

.map-container__canvas {
  height: 650px;
  width: 100%;
}

.map-container__loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  background: $mp-surface;
  padding: 16px 22px;
  border-radius: 10px;
  border: 1px solid $mp-border;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
}

// ──────────── FABs ────────────
.map-fabs {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.map-fab {
  width: 48px;
  height: 48px;
  border: 1px solid $mp-border;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  transition: all 200ms ease;
  background: $mp-primary;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  }

  &--calendar {
    background: $mp-primary;
    border-color: $mp-primary;
    &:hover { background: darken($mp-primary, 6%); }
  }

  &--toggle {
    background: $mp-cta;
    border-color: $mp-cta;
    &:hover { background: darken($mp-cta, 6%); }
  }
}

// ──────────── ANIMATIONS ────────────
@keyframes mp-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.85); }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

// ──────────── DARK MODE ────────────
.dark-layout .map-page ::v-deep {
  background: #161616;
  color: #d0d2d6;

  .map-dash-header,
  .map-section,
  .map-loading {
    background: #1f1f1f;
    border-color: #2c2c2c;
  }
  .map-title { color: #e4e6eb; }
  .map-section__title { color: #e4e6eb; }
  .map-stat-pill { background: #161616; border-color: #2c2c2c; }
  .map-stat-pill strong { color: #e4e6eb; }
  .map-kpis { border-top-color: #2c2c2c; }
  .map-kpi { background: #161616; border-color: #2c2c2c; }
  .map-kpi__value { color: #e4e6eb; }

  .map-section__header { border-bottom-color: #2c2c2c; }
  .map-filters__reset {
    background: #1f1f1f;
    border-color: #2c2c2c;
    color: #8e9099;
  }
  .map-container { background: #161616; border-color: #2c2c2c; }
  .map-container__loading { background: #242424; border-color: #2c2c2c; }
}
</style>